import { Button, Card, Field, Notice, PageHeader, StatusPill, type StatusTone } from '@homecare/ui';
import { CAREGIVER_STATUS_TRANSITIONS, type CaregiverStatus } from '@homecare/domain';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { ApiRequestError } from '../../lib/api';
import { useApi } from '../../lib/api-hooks';
import { useHasPermission } from '../../lib/session';

import { formatPayRate } from './caregiver-list';
import { CompliancePanel } from './compliance';
import { CredentialsCard } from './credentials';
import { AvailabilityCard } from './availability';
import { CaregiverRelationshipsCard } from './client-relationships';
import { ShiftReadiness } from './shift-readiness';

/**
 * One caregiver (slice-02 §7).
 *
 * ## Sections, not tabs
 *
 * §7 sketched Overview, Credentials, Availability, Restrictions and History as
 * tabs. Session 8 makes them sections of one page, under a strip that answers
 * "can they take a shift" from all three at once: a coordinator deciding that
 * needs compliance, the rota and any exclusion together, and behind tabs the
 * exclusion is the third click — the one answer nobody can override. See
 * `shift-readiness.tsx`.
 *
 * ## The checklist is on the record, next to the button it gates
 *
 * §17.3 says a caregiver cannot reach ACTIVE while a `blocks_scheduling` item is
 * outstanding. The API refuses and names the items, and that refusal is the
 * authority — but a person who has to press a button to find out what is
 * missing has been made to guess. The checklist is rendered beside the status
 * action so the answer is visible before the attempt, and the refusal is the
 * backstop rather than the interface.
 */

const STATUS_TONE: Record<string, StatusTone> = {
  APPLICANT: 'progress',
  ONBOARDING: 'progress',
  ACTIVE: 'verified',
  INACTIVE: 'inactive',
  TERMINATED: 'inactive',
  DO_NOT_RETURN: 'critical',
};

const STATUS_LABEL: Record<string, string> = {
  APPLICANT: 'Applicant',
  ONBOARDING: 'Onboarding',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  TERMINATED: 'Terminated',
  DO_NOT_RETURN: 'Do not return',
};

/** Verbs, keyed by the move. The destination alone does not name the act. */
const MOVE_LABEL: Record<string, string> = {
  'APPLICANT->ONBOARDING': 'Start onboarding',
  'ONBOARDING->ACTIVE': 'Activate',
  'ACTIVE->INACTIVE': 'Put on hold',
  'INACTIVE->ACTIVE': 'Return to active',
};

interface CaregiverDetailData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  employeeNumber: string | null;
  status: string;
  employmentType: string;
  disciplines: string[];
  languages: string[];
  hireDate: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  maxWeeklyHours: number | null;
  npi: string | null;
  stateRegistryId: string | null;
  homeLine1: string | null;
  homeCity: string | null;
  homeState: string | null;
  homePostalCode: string | null;
  homeGeocodeAccuracy: string | null;
  matching: {
    hasVehicle: boolean;
    willTransportClient: boolean;
    smokingOk: boolean;
    petsOk: boolean;
    maxTravelMinutes: number | null;
  };
  /** Absent, not null, without `caregiver:read_pay`. */
  basePayRateCents?: number;
}

interface OnboardingView {
  items: {
    id: string;
    label: string;
    blocksScheduling: boolean;
    completed: boolean;
  }[];
  blocksActivation: boolean;
}

/**
 * The moves offered from here, discharge-style terminal ones excluded.
 *
 * Generated from `CAREGIVER_STATUS_TRANSITIONS` — the same table the API
 * validates against — so a move the machine would refuse is never rendered.
 * TERMINATED and DO_NOT_RETURN are filtered out: they end employment, hold
 * `caregiver:terminate` rather than `caregiver:update`, and get their own
 * control.
 */
/** The checklist items an activation was refused for, if that is what the error is. */
export function outstandingOf(error: unknown): string[] | null {
  if (!(error instanceof ApiRequestError) || error.code !== 'VALIDATION_FAILED') return null;
  const items = error.details['outstanding'];
  return items && items.length > 0 ? items : null;
}

export function legalCaregiverMoves(
  from: CaregiverStatus,
): { to: CaregiverStatus; label: string }[] {
  return (CAREGIVER_STATUS_TRANSITIONS[from] ?? [])
    .filter((to) => to !== 'TERMINATED' && to !== 'DO_NOT_RETURN')
    .map((to) => ({
      to,
      label: MOVE_LABEL[`${from}->${to}`] ?? `Move to ${to.toLowerCase().replace('_', ' ')}`,
    }));
}

export function CaregiverDetail({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();

  const canSeePay = useHasPermission('caregiver:read_pay');
  const canUpdate = useHasPermission('caregiver:update');
  const canTerminate = useHasPermission('caregiver:terminate');

  const detail = useQuery({
    queryKey: ['caregivers', 'detail', caregiverId],
    queryFn: () => api.get<CaregiverDetailData>(`/v1/caregivers/${caregiverId}`),
  });

  const onboarding = useQuery({
    queryKey: ['caregivers', 'onboarding', caregiverId],
    queryFn: () => api.get<OnboardingView>(`/v1/caregivers/${caregiverId}/onboarding`),
  });

  const [open, setOpen] = useState<{ to: CaregiverStatus; label: string } | 'terminate' | null>(
    null,
  );
  const [reason, setReason] = useState('');
  const [doNotReturn, setDoNotReturn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['caregivers', 'detail', caregiverId] }),
      queryClient.invalidateQueries({ queryKey: ['caregivers', 'onboarding', caregiverId] }),
      queryClient.invalidateQueries({ queryKey: ['caregivers', 'list'] }),
    ]);
  };

  if (detail.isPending) {
    return (
      <Card>
        <p className="text-ink-3 py-10 text-center text-sm">Loading the caregiver…</p>
      </Card>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <Card>
        <ErrorNote
          error={detail.error}
          nextStep="Go back to the roster and open the caregiver again."
        />
      </Card>
    );
  }

  const person = detail.data;
  const status = person.status as CaregiverStatus;
  const moves = canUpdate ? legalCaregiverMoves(status) : [];

  const submit = async (): Promise<void> => {
    if (!open) return;

    setSaving(true);
    setFailure(null);

    try {
      if (open === 'terminate') {
        await api.send('POST', `/v1/caregivers/${caregiverId}/terminate`, {
          reason: reason.trim(),
          doNotReturn,
        });
      } else {
        await api.send('POST', `/v1/caregivers/${caregiverId}/status`, {
          toStatus: open.to,
          reason: reason.trim(),
        });
      }

      await refresh();
      setOpen(null);
      setReason('');
      setDoNotReturn(false);
    } catch (error) {
      setFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const completeItem = async (itemId: string): Promise<void> => {
    setFailure(null);

    try {
      await api.send('POST', `/v1/caregivers/${caregiverId}/onboarding/${itemId}/complete`, {});
      await refresh();
    } catch (error) {
      setFailure(error);
    }
  };

  return (
    <div className="grid gap-5">
      <PageHeader
        title={`${person.firstName} ${person.lastName}`}
        meta={
          person.employeeNumber
            ? `Employee ${person.employeeNumber} · ${person.email}`
            : person.email
        }
        action={
          <StatusPill tone={STATUS_TONE[person.status] ?? 'inactive'}>
            {STATUS_LABEL[person.status] ?? person.status}
          </StatusPill>
        }
      />

      <ShiftReadiness caregiverId={caregiverId} />

      {/*
        The status panel is wrapped in a plain div carrying the testid, rather
        than putting it on `Card` — which does not forward arbitrary props, and
        teaching a shared primitive to forward them for one test would be the
        wrong direction of change.

        The wrapper has to cover the form as well as the buttons. The submit
        button carries the same label as the button that opened it ("End
        employment" opens the form and then confirms it), so a locator scoped to
        the button row alone matches only the opener — and clicking that a
        second time reopens the form and clears the reason somebody just typed.
      */}
      {(moves.length > 0 || (canTerminate && status !== 'DO_NOT_RETURN')) && (
        <div data-testid="caregiver-status">
          <Card>
            <h2 className="text-ink text-lg font-bold">Status</h2>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {moves.map((move) => (
                <Button
                  key={move.to}
                  size="sm"
                  onClick={() => {
                    setOpen(move);
                    setReason('');
                    setFailure(null);
                  }}
                >
                  {move.label}
                </Button>
              ))}

              {canTerminate && status !== 'TERMINATED' && status !== 'DO_NOT_RETURN' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOpen('terminate');
                    setReason('');
                    setDoNotReturn(false);
                    setFailure(null);
                  }}
                >
                  End employment
                </Button>
              )}
            </div>

            {/*
            The refusal, before the attempt.

            The API decides and names the outstanding items, and that stays the
            authority. Saying so here as well means somebody sees why Activate
            will not work without pressing it — and the message stays true
            because both come from the same `blocksActivation`.
          */}
            {onboarding.data?.blocksActivation && status === 'ONBOARDING' && (
              <p className="text-ink-3 mt-3 text-sm">
                Activating needs the blocking items below completed first.
              </p>
            )}

            {open && (
              <div className="border-line bg-surface-2 mt-4 grid gap-3 rounded-md border p-4">
                {/*
                  Activation refused for an open checklist gets its own notice.
                  Through the generic one it read "Some of what you entered is
                  not valid. Check the highlighted fields" and then "Check the
                  reason and try again" — two instructions, both wrong: nothing
                  typed was invalid, and the reason was fine. Found walking
                  slice-02 exit criterion 1.
                */}
                {outstandingOf(failure) ? (
                  <Notice
                    tone="warning"
                    title="Not yet — the checklist below still has blocking items."
                  >
                    <ul className="grid gap-0.5">
                      {outstandingOf(failure)!.map((label) => (
                        <li key={label}>{label}</li>
                      ))}
                    </ul>
                    <p className="mt-1">Mark them done below, then activate.</p>
                  </Notice>
                ) : (
                  <ErrorNote error={failure} nextStep="Check the reason and try again." />
                )}

                <Field
                  label="Reason"
                  hint="It goes on the record with your name."
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  data-testid="caregiver-status-reason"
                />

                {open === 'terminate' && (
                  <label className="text-ink-2 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={doNotReturn}
                      onChange={(event) => setDoNotReturn(event.target.checked)}
                    />
                    {/* One decision, one moment. Asking separately is how it gets
                      forgotten on the record where it mattered. */}
                    Do not re-hire this person
                  </label>
                )}

                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => void submit()}
                    pending={saving}
                    {...(reason.trim()
                      ? {}
                      : ({ disabled: true, disabledReason: 'Enter a reason first.' } as const))}
                  >
                    {open === 'terminate' ? 'End employment' : open.label}
                  </Button>
                  <Button variant="ghost" onClick={() => setOpen(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/*
        Compliance above the paperwork, and the credential list below it.

        The order is the argument: the first thing a coordinator wants is the
        answer, and the second is why. Putting the cached statuses first would
        make them the thing people read, and R-2b is that they are not the thing
        that decides.
      */}
      {/*
        Then the three things a shift depends on, in the order the strip at the
        top names them: compliance, the rota, and the clients they must not be
        sent to. The credential list follows them because it is the evidence
        behind the first, not a fourth answer.
      */}
      <div id="compliance" className="scroll-mt-6">
        <CompliancePanel caregiverId={caregiverId} />
      </div>

      <div id="availability" className="scroll-mt-6">
        <AvailabilityCard caregiverId={caregiverId} />
      </div>

      <div id="restrictions" className="scroll-mt-6">
        <CaregiverRelationshipsCard caregiverId={caregiverId} />
      </div>

      <div id="credentials" className="scroll-mt-6">
        <CredentialsCard caregiverId={caregiverId} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <h2 className="text-ink text-lg font-bold">Employment</h2>
          <dl className="mt-3 grid gap-2">
            <Row label="Type" value={person.employmentType.replace(/_/g, ' ').toLowerCase()} />
            <Row label="Hired" value={person.hireDate ?? 'Not recorded'} />
            <Row
              label="Weekly cap"
              value={person.maxWeeklyHours === null ? 'None' : `${person.maxWeeklyHours} hours`}
            />
            {/*
              Rendered on the permission, not on the value. A "—" in a Pay row
              would tell a scheduler there is a rate they may not see, which is
              one bit more than §10.4 gives them.
            */}
            {canSeePay && <Row label="Pay rate" value={formatPayRate(person.basePayRateCents)} />}
            {person.terminationDate && (
              <Row
                label="Ended"
                value={`${person.terminationDate} — ${person.terminationReason}`}
              />
            )}
          </dl>
        </Card>

        <Card>
          <h2 className="text-ink text-lg font-bold">What they can do</h2>
          <dl className="mt-3 grid gap-2">
            <Row
              label="Disciplines"
              value={person.disciplines.map((d) => d.replace(/_/g, ' ').toLowerCase()).join(', ')}
            />
            <Row label="Languages" value={person.languages.join(', ') || 'Not recorded'} />
            <Row label="Has a vehicle" value={person.matching.hasVehicle ? 'Yes' : 'No'} />
            <Row
              label="Will transport"
              value={person.matching.willTransportClient ? 'Yes' : 'No'}
            />
            <Row label="Smoking household" value={person.matching.smokingOk ? 'OK' : 'No'} />
            <Row label="Pets" value={person.matching.petsOk ? 'OK' : 'No'} />
            <Row
              label="Max travel"
              value={
                person.matching.maxTravelMinutes === null
                  ? 'No limit'
                  : `${person.matching.maxTravelMinutes} minutes`
              }
            />
          </dl>
        </Card>

        <Card>
          <h2 className="text-ink text-lg font-bold">Home address</h2>
          {person.homeLine1 ? (
            <>
              <p className="text-ink-2 mt-3 text-sm">
                {person.homeLine1}, {person.homeCity}, {person.homeState} {person.homePostalCode}
              </p>
              {/*
                Visible when it fails, never absent (§11.3). A caregiver with no
                point is one §14.4's travel matching drops without saying why,
                and "we could not place this address" is the sentence that gets
                it corrected.
              */}
              {person.homeGeocodeAccuracy === 'FAILED' && (
                <p className="text-warning mt-2 text-sm">
                  We could not place this address on the map, so travel-time matching will skip this
                  caregiver. Check the street and postcode.
                </p>
              )}
            </>
          ) : (
            <p className="text-ink-3 mt-3 text-sm">
              No home address recorded. Travel-time matching needs one.
            </p>
          )}
        </Card>

        <Card>
          <h2 className="text-ink text-lg font-bold">Onboarding</h2>
          <p className="text-ink-3 mt-1 text-sm">
            A caregiver cannot go active while a blocking item is outstanding.
          </p>

          {onboarding.isPending ? (
            <p className="text-ink-3 py-6 text-center text-sm">Loading the checklist…</p>
          ) : (onboarding.data?.items ?? []).length === 0 ? (
            <p className="text-ink-3 mt-3 text-sm">
              This agency has no onboarding checklist configured.
            </p>
          ) : (
            <ul className="mt-3 grid gap-2" data-testid="onboarding-checklist">
              {(onboarding.data?.items ?? []).map((item) => (
                <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm">
                    <span className={item.completed ? 'text-ink-3 line-through' : 'text-ink'}>
                      {item.label}
                    </span>
                    {item.blocksScheduling && !item.completed && (
                      <span className="text-warning ml-2 text-xs">blocks activation</span>
                    )}
                  </span>

                  {canUpdate && !item.completed && (
                    <Button variant="ghost" size="sm" onClick={() => void completeItem(item.id)}>
                      Mark done
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-3 text-sm">{label}</dt>
      <dd className="text-ink text-sm">{value}</dd>
    </div>
  );
}
